import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A vertical box plot, also known as a box-and-whisker plot, is a statistical chart used to
          display the distribution of a dataset. It shows the minimum, first quartile (Q1), median
          (Q2), third quartile (Q3), and maximum values of the data, along with potential outliers.
          In a vertical box plot, the data is represented along the vertical axis, with the box
          itself illustrating the interquartile range (IQR) and the whiskers extending from the box
          to show the range of data within 1.5 times the IQR. Outliers, if any, are typically shown
          as individual points outside the whiskers. This chart is useful for comparing
          distributions between different categories or datasets, identifying data spread, central
          tendency, and outliers.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Vertical box plots, also known as box-and-whisker plots, are valuable for summarizing data
          distributions and identifying trends. Here are three significant uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Data Distribution:</strong> They effectively display the range,
            median, and quartiles of a dataset, providing a clear overview of data variability.
          </li>
          <li>
            <strong>Identifying Outliers:</strong> Vertical box plots highlight outliers through
            data points outside the whiskers, aiding in data analysis and anomaly detection.
          </li>
          <li>
            <strong>Comparing Groups:</strong> They facilitate side-by-side comparison of
            distributions across multiple categories or datasets.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Vertical box plots are powerful tools for summarizing distributions, but they come with
          specific limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Loss of Distribution Detail:</strong> Vertical box plots summarize the data
            through quartiles, but they omit the finer details of the distribution, such as the
            exact shape or the presence of multiple peaks. This can lead to a loss of critical
            insights, especially when the data has a complex distribution.
          </li>
          <li>
            <strong>Misleading with Small Sample Sizes:</strong> For small datasets, box plots can
            be misleading as the statistical summary might not accurately represent the variability
            or central tendency of the data, making it difficult to interpret the true nature of the
            data.
          </li>
          <li>
            <strong>Limited in Visualizing Group Differences:</strong> While box plots can compare
            distributions across groups, they can become cluttered when many groups are involved.
            This reduces the clarity of comparisons and makes it harder to distinguish between the
            distributions.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in vertical box plots enhances their interpretability and visual appeal.
          Using consistent colors for different data groups aids in comparison, while varying shades
          can highlight specific patterns or outliers. Transparent or lighter colors for box fills
          prevent overlap from obscuring details, and bold, contrasting hues can emphasize medians
          or other critical values. Thoughtful color choices ensure the chart remains clear and
          informative.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Vertical box plots are valuable for summarizing data distributions, but they face
          challenges that can impact their interpretability:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Misinterpretation of Elements:</strong> Non-expert audiences may struggle to
            understand the components of a box plot, such as quartiles, whiskers, and outliers,
            leading to potential misinterpretation of the data.
          </li>
          <li>
            <strong>Sensitivity to Outliers:</strong> Outliers can disproportionately affect the
            visual appearance of the plot, potentially skewing perception of the overall data
            distribution.
          </li>
          <li>
            <strong>Comparing Multiple Groups:</strong> When comparing multiple groups, vertical box
            plots can become crowded, making it challenging to distinguish subtle differences
            between categories or datasets.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
